import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { api } from '@/api/client';
import { PaginatedResponse } from '@/types/api.types';
// Requisition volume is the `delivered` term of ClientBalanceQuery, so any
// mutation that moves SuppliedAmount or a requisition's status also moves the
// client balance that GET /api/client/list serves. The Delivery Scheduler
// renders that balance as "saldo" right beside these requisitions' bars, so
// the two must be invalidated together or the saldo lags the bars by up to
// the 5-minute staleTime.
import { clientKeys } from '@/api/hooks/useClients';

// API response wrapper type
interface ApiResponse<T> {
  success: boolean;
  message?: string;
  data: T;
}

// API Requisition response type
interface ApiRequisition {
  requisitionId: number;
  folio: string | null;
  clientId: number;
  clientName: string | null;
  locationId: number;
  locationName: string | null;
  requestDate: string;
  amount: number;
  suppliedAmount: number;
  status: string;
  createdDate: string;
  supplied: boolean;
  suppliedDate: string | null;
  startDate: string | null;
  endDate: string | null;
  completeDate: string | null;
  active: boolean;
  rejectionReason?: string | null;
  rejectedDate?: string | null;
  importedManually?: boolean | null;
  importedDate?: string | null;
  importFileName?: string | null;
  pallets?: { epcId: string; epc: string; scanDate: string }[];
}

export type RequisitionStatus = 'Requested' | 'Approved' | 'InProgress' | 'Completed' | 'Rejected';

export interface Requisition {
  id: number;
  folio: string;
  clientId: number;
  clientName: string;
  clientCode: string;
  locationId: number;
  locationName: string;
  requestedQuantity: number;
  suppliedQuantity: number;
  status: RequisitionStatus;
  active: boolean;
  rejectionReason?: string;
  rejectedDate?: string;
  /**
   * True when some or all of this requisition's pallets were registered from an
   * uploaded file instead of scanned on the handheld. Worth surfacing because a
   * manual import asserts a delivery nobody scanned — the rows it writes are
   * otherwise identical to a handheld finalize.
   */
  importedManually?: boolean;
  importedDate?: string;
  importFileName?: string;
  requestedDate: string;
  approvedDate?: string;
  completedDate?: string;
  deliveryDate?: string;
  notes?: string;
  createdById: number;
  createdByName: string;
  createdAt: string;
  updatedAt: string;
}

export interface RequisitionPallet {
  epcId: string;
  epc: string;
  scanDate: string;
}

export interface RequisitionSearchParams {
  search?: string;
  clientId?: number;
  status?: RequisitionStatus;
  includeInactive?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  /**
   * Optional ISO window bounds. Omit for no filter — the server has no
   * default window. The server matches on range OVERLAP (a requisition is
   * kept when it started before `toDate` and had not completed before
   * `fromDate`), so long-running deliveries spanning the window are kept.
   */
  fromDate?: string;
  toDate?: string;
}

export interface CreateRequisitionRequest {
  clientId: number;
  locationId: number;
  requestedQuantity: number;
  notes?: string;
}

export interface UpdateRequisitionRequest {
  id: number;
  requestedQuantity?: number;
  notes?: string;
}

export interface SupplyRequisitionRequest {
  id: number;
  suppliedQuantity: number;
}

export interface RequisitionStats {
  pending: number;
  approved: number;
  inProgress: number;
  completed: number;
  rejected: number;
  totalRequested: number;
  totalSupplied: number;
}

// Query keys
export const requisitionKeys = {
  all: ['requisitions'] as const,
  lists: () => [...requisitionKeys.all, 'list'] as const,
  list: (params: RequisitionSearchParams) => [...requisitionKeys.lists(), params] as const,
  details: () => [...requisitionKeys.all, 'detail'] as const,
  detail: (id: number) => [...requisitionKeys.details(), id] as const,
  stats: () => [...requisitionKeys.all, 'stats'] as const,
};

// Map API status string to our status type
function mapStatus(status: string): RequisitionStatus {
  switch (status) {
    case 'Requested': return 'Requested';
    case 'Approved': return 'Approved';
    case 'InProgress': return 'InProgress';
    case 'Completed': return 'Completed';
    case 'Rejected': return 'Rejected';
    default:
      console.warn('Unknown requisition status:', status);
      return 'Requested';
  }
}

// Transform API requisition to our interface
function transformRequisition(apiReq: ApiRequisition): Requisition {
  return {
    id: apiReq.requisitionId,
    folio: apiReq.folio || `REQ-${apiReq.requisitionId}`,
    clientId: apiReq.clientId,
    clientName: apiReq.clientName || '',
    clientCode: String(apiReq.clientId),
    locationId: apiReq.locationId,
    locationName: apiReq.locationName || '',
    requestedQuantity: apiReq.amount,
    suppliedQuantity: apiReq.suppliedAmount,
    status: mapStatus(apiReq.status),
    active: apiReq.active ?? true,
    rejectionReason: apiReq.rejectionReason ?? undefined,
    rejectedDate: apiReq.rejectedDate ?? undefined,
    importedManually: apiReq.importedManually ?? false,
    importedDate: apiReq.importedDate ?? undefined,
    importFileName: apiReq.importFileName ?? undefined,
    requestedDate: apiReq.requestDate,
    approvedDate: apiReq.startDate || undefined,
    completedDate: apiReq.completeDate || undefined,
    notes: undefined,
    createdById: 0,
    createdByName: '',
    createdAt: apiReq.createdDate,
    updatedAt: apiReq.createdDate,
  };
}

// Search requisitions
export function useRequisitions(params: RequisitionSearchParams) {
  return useQuery({
    queryKey: requisitionKeys.list(params),
    queryFn: async () => {
      const response = await api.post<ApiResponse<{ data: ApiRequisition[]; totalRecords: number }>>('/api/requisition/search', {
        searchStr: params.search,
        clientId: params.clientId,
        status: params.status,
        includeInactive: params.includeInactive || false,
        page: params.page || 1,
        pageSize: params.pageSize || 25,
        fromDate: params.fromDate,
        toDate: params.toDate,
      });
      const apiData = response.data.data;
      return {
        items: apiData.data.map(transformRequisition),
        totalCount: apiData.totalRecords,
        totalPages: Math.ceil(apiData.totalRecords / (params.pageSize || 25)),
        page: params.page || 1,
        pageSize: params.pageSize || 25,
      } as PaginatedResponse<Requisition>;
    },
    // Keep the previous page/window on screen while the next one loads.
    // Without this, any key change (page number, or the scheduler's date
    // window) drops the query to `isLoading`, and callers that gate their
    // whole UI on that flag unmount their toolbars and reset scroll position.
    placeholderData: keepPreviousData,
  });
}

// Get requisition stats (calculated from search results)
export function useRequisitionStats() {
  return useQuery({
    queryKey: requisitionKeys.stats(),
    queryFn: async () => {
      // Fetch all requisitions to calculate stats (no pagination)
      const response = await api.post<ApiResponse<{ data: ApiRequisition[]; totalRecords: number }>>('/api/requisition/search', {
        page: 1,
        pageSize: 10000,
      });
      const requisitions = response.data.data.data;

      const activeReqs = requisitions.filter(r => r.status !== 'Rejected');

      const stats: RequisitionStats = {
        pending: requisitions.filter(r => r.status === 'Requested').length,
        approved: requisitions.filter(r => r.status === 'Approved').length,
        inProgress: requisitions.filter(r => r.status === 'InProgress').length,
        completed: requisitions.filter(r => r.status === 'Completed').length,
        rejected: requisitions.filter(r => r.status === 'Rejected').length,
        totalRequested: activeReqs.reduce((sum, r) => sum + r.amount, 0),
        totalSupplied: activeReqs.reduce((sum, r) => sum + r.suppliedAmount, 0),
      };
      return stats;
    },
  });
}

// Get single requisition
export function useRequisition(id: number, enabled = true) {
  return useQuery({
    queryKey: requisitionKeys.detail(id),
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiRequisition>>('/api/requisition/get', {
        params: { requisitionId: id },
      });
      return transformRequisition(response.data.data);
    },
    enabled: enabled && id > 0,
  });
}

// Get pallets for a requisition
export function useRequisitionPallets(id: number | null) {
  return useQuery({
    queryKey: [...requisitionKeys.detail(id ?? 0), 'pallets'],
    queryFn: async () => {
      const response = await api.get<ApiResponse<ApiRequisition>>('/api/requisition/get', {
        params: { requisitionId: id },
      });
      const pallets = response.data.data.pallets ?? [];
      return pallets.map((p): RequisitionPallet => ({
        epcId: p.epcId,
        epc: p.epc,
        scanDate: p.scanDate,
      }));
    },
    enabled: id !== null && id > 0,
  });
}

// Create requisition
export function useCreateRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateRequisitionRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/requisition/save', {
        requisitionId: 0,
        clientId: data.clientId,
        locationId: data.locationId,
        requestDate: new Date().toISOString(),
        amount: data.requestedQuantity,
      });
      return response.data.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Update requisition
export function useUpdateRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: UpdateRequisitionRequest) => {
      // First get current requisition
      const getResponse = await api.get<ApiResponse<ApiRequisition>>('/api/requisition/get', {
        params: { requisitionId: data.id },
      });
      const current = getResponse.data.data;

      const response = await api.post<ApiResponse<number>>('/api/requisition/save', {
        requisitionId: data.id,
        clientId: current.clientId,
        locationId: current.locationId,
        requestDate: current.requestDate,
        amount: data.requestedQuantity ?? current.amount,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.detail(variables.id) });
    },
  });
}

// Approve requisition
export function useApproveRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/updateStatus', {
        requisitionId: id,
        status: 2, // Approved
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Reject requisition
export function useRejectRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, reason }: { id: number; reason?: string }) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/updateStatus', {
        requisitionId: id,
        status: 3, // Rejected
        reason,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Return requisition to Requested status (reopen for editing)
export function useReopenRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/updateStatus', {
        requisitionId: id,
        status: 1, // Requested
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Edit requisition (admin) — Client / Location / Amount / Folio
export interface EditRequisitionRequest {
  id: number;
  clientId: number;
  locationId: number;
  requestDate: string;
  amount: number;
  folio?: string;
}

export function useEditRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: EditRequisitionRequest) => {
      const response = await api.post<ApiResponse<number>>('/api/requisition/save', {
        requisitionId: data.id,
        clientId: data.clientId,
        locationId: data.locationId,
        requestDate: data.requestDate,
        amount: data.amount,
        folio: data.folio,
      });
      return response.data.data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Start supplying requisition
export function useStartRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/updateStatus', {
        requisitionId: id,
        status: 4, // InProgress
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Supply pallets to requisition (updates supplied amount)
export function useSupplyRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: SupplyRequisitionRequest) => {
      // Note: The API doesn't have a dedicated supply endpoint
      // This would need to be implemented in the backend
      console.warn('Supply requisition - needs backend implementation');
      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Complete requisition
export function useCompleteRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/updateStatus', {
        requisitionId: id,
        status: 5, // Completed
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Delete requisition (only if Requested status) - soft delete via status
export function useDeleteRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/deactivate', {
        requisitionId: id,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// Manually assign an EPC/pallet to an existing requisition
export function useAssignPallet() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: { requisitionId: number; epcId: string; oldEpcId?: string }) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/assign-pallet', {
        requisitionId: data.requisitionId,
        epcId: data.epcId,
        oldEpcId: data.oldEpcId || undefined,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.details() });
    },
  });
}

// Deactivate requisition (all statuses except Completed; reverts assets for InProgress)
export function useDeactivateRequisition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await api.post<ApiResponse<unknown>>('/api/requisition/deactivate', {
        requisitionId: id,
      });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
      queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// ── Manual import of scanned pallets (fallback for the handheld flow) ──
// Uploads the same JSON/CSV the handheld exports; the backend drives the
// shared fulfillment service, so the result equals a handheld finalize.
// Validation is all-or-nothing: a dry-run / commit failure comes back as
// HTTP 200 with ok:false plus the offending EPC list (rendered inline).
export interface RequisitionImportTotals {
  totalScanned: number;
  toImport: number;
  skippedDuplicate: number;
  statusConflicts: number;
}

/**
 * A pallet the import refuses to deliver because its current state contradicts
 * the delivery — it is still out with another client, or damaged.
 *
 * The import enforces this where the handheld does not: a scan is physical
 * evidence the pallet is in the operator's hand, a file is not. `folio` and
 * `clientName` say WHERE the pallet went, which is what makes the conflict
 * actionable instead of just blocking.
 */
export interface RequisitionImportStatusConflict {
  epcId: string;
  assetId: string;
  currentStatus: number;
  currentStatusName: string;
  requisitionId?: number | null;
  folio?: string | null;
  clientId?: number | null;
  clientName?: string | null;
}

export interface RequisitionImportResponse {
  ok: boolean;
  dryRun: boolean;
  totals: RequisitionImportTotals;
  code?: string;
  message?: string;
  missingEpcIds?: string[];
  statusConflicts?: RequisitionImportStatusConflict[];
}

export function useImportRequisitionPallets() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requisitionId,
      file,
      dryRun = false,
    }: {
      requisitionId: number;
      file: File;
      dryRun?: boolean;
    }) => {
      const formData = new FormData();
      formData.append('file', file);
      const response = await api.post<RequisitionImportResponse>(
        `/api/requisition/${requisitionId}/import-pallets?dryRun=${dryRun ? 'true' : 'false'}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      if (!data.dryRun && data.ok) {
        queryClient.invalidateQueries({ queryKey: requisitionKeys.lists() });
        queryClient.invalidateQueries({ queryKey: requisitionKeys.stats() });
        queryClient.invalidateQueries({ queryKey: clientKeys.all });
        queryClient.invalidateQueries({ queryKey: requisitionKeys.details() });
        queryClient.invalidateQueries({ queryKey: requisitionKeys.detail(variables.requisitionId) });
      }
    },
  });
}
